// Owner OAuth connections — click-to-connect for the social/email providers
// (LinkedIn, Microsoft/Outlook, X, Meta Facebook Page, Reddit). One card per
// provider driven by GET /api/ops/connections (react-query, ~10s ops polling).
// Connect POSTs /api/ops/connections/{provider}/connect and hands the browser
// to the returned authorize_url; the OAuth callback lands back here with
// ?connected={provider} or ?error=... which we toast/banner then scrub.
// Tokens live ENCRYPTED in Mongo and auto-refresh server-side — this page only
// ever sees status + account labels, never secrets (env-var fallback stays).
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plug, Link2, Unplug, AlertTriangle } from "lucide-react";

import MockBadge from "@/components/MockBadge";
import { connections } from "@/lib/api";
import { fmtTime } from "@/lib/format";

// Card order is fixed; `key` must match the backend provider slug in
// /api/ops/connections rows and the {provider} path param. Marks are plain
// text (design rule: no new icon deps for brand logos).
const PROVIDERS = [
  { key: "linkedin", name: "LinkedIn", mark: "in" },
  { key: "microsoft", name: "Microsoft (Outlook)", mark: "MS" },
  { key: "x", name: "X (Twitter)", mark: "𝕏" },
  { key: "meta", name: "Meta (Facebook Page)", mark: "f" },
  { key: "reddit", name: "Reddit", mark: "r/" },
];

const providerName = (key) => PROVIDERS.find((p) => p.key === key)?.name || key;

// Which backend env vars unlock a provider app — used for the muted
// "App not configured" hint. Prefer the names the API sends (required_env);
// otherwise derive them from the provider slug.
function envNames(key, row) {
  const fromApi = row?.required_env || row?.env_vars || row?.env;
  if (Array.isArray(fromApi) && fromApi.length) return fromApi;
  const p = String(key).toUpperCase();
  return [`${p}_CLIENT_ID`, `${p}_CLIENT_SECRET`];
}

// Same tone recipe as StatusPill, with the two extra connection-only tones.
const TONES = {
  connected: { fg: "rgb(32,138,78)", bg: "rgba(32,138,78,0.10)", bd: "rgba(32,138,78,0.30)" },
  expiring: { fg: "rgb(176,116,18)", bg: "rgba(176,116,18,0.10)", bd: "rgba(176,116,18,0.30)" },
  error: { fg: "rgb(196,52,44)", bg: "rgba(196,52,44,0.10)", bd: "rgba(196,52,44,0.30)" },
  not_connected: { fg: "rgb(108,96,80)", bg: "rgba(38,31,24,0.05)", bd: "rgba(190,176,152,0.45)" },
  not_configured: { fg: "rgb(140,128,110)", bg: "rgba(38,31,24,0.03)", bd: "rgba(190,176,152,0.30)" },
};

// Normalise whatever the API says into one of the five card states. The
// backend is the source of truth; the expires_at check is only a client-side
// safety net (a token inside 72h of expiry paints amber even if the worker
// hasn't re-statused it yet).
const EXPIRING_WINDOW_MS = 72 * 3600 * 1000;
function deriveStatus(row) {
  if (!row) return "not_connected";
  if (row.configured === false) return "not_configured";
  const s = String(row.status || "").toLowerCase();
  if (["error", "failed", "revoked", "invalid"].includes(s) || row.error || row.last_error) return "error";
  if (["expiring", "expiring_soon", "expires_soon"].includes(s)) return "expiring";
  const connected =
    ["connected", "active", "ok", "healthy"].includes(s) ||
    (!s && (row.connected === true || !!row.account_label));
  if (connected) {
    // Only NON-refreshable connections (e.g. LinkedIn) should paint 'expiring'
    // off the access-token clock. Refreshable ones (offline_access -> Outlook /
    // X / Reddit / Meta) are auto-renewed by the worker every cycle, so their
    // short ~1h access-token expiry is NOT a reconnect signal — trust the
    // backend status, which only flips to 'expiring' near a real, final expiry.
    if (row.expires_at && row.supports_refresh === false) {
      const ms = new Date(row.expires_at).getTime() - Date.now();
      if (!Number.isNaN(ms) && ms < EXPIRING_WINDOW_MS) return "expiring";
    }
    return "connected";
  }
  return "not_connected";
}

function pillLabel(status, row) {
  if (status === "connected") return `Connected as ${row?.account_label || "owner"}`;
  if (status === "expiring") return "Expiring soon — reconnect";
  if (status === "error") return "Error";
  if (status === "not_configured") return "App not configured";
  return "Not connected";
}

function StatusBadge({ status, row, providerKey }) {
  const tone = TONES[status] || TONES.not_connected;
  const text = pillLabel(status, row);
  return (
    <span
      data-testid={`connection-status-${providerKey}`}
      title={text}
      className="inline-flex items-center gap-1.5 mono align-middle max-w-full"
      style={{
        background: tone.bg,
        color: tone.fg,
        border: `1px solid ${tone.bd}`,
        padding: "2px 8px",
        fontSize: 10,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        lineHeight: 1.3,
        whiteSpace: "nowrap",
      }}
    >
      <span
        className="pj-pulse flex-shrink-0"
        style={{ width: 6, height: 6, background: tone.fg, borderRadius: 999, display: "inline-block" }}
      />
      <span className="truncate">{text}</span>
    </span>
  );
}

// window.location.assign behind a stubbable seam — jsdom marks Location
// members [LegacyUnforgeable], so tests can't spy on assign directly.
export const oauthRedirect = {
  go: (url) => window.location.assign(url),
};

const BTN_PRIMARY_STYLE = {
  background: "linear-gradient(90deg, rgb(169,130,28), rgb(158,92,44))",
  color: "rgb(252,249,243)",
};
const BTN_SECONDARY_STYLE = {
  background: "rgba(244,239,230,0.9)",
  border: "1px solid rgba(190,176,152,0.6)",
  color: "rgb(38,31,24)",
};

export default function Connections() {
  const qc = useQueryClient();
  // Banner for an OAuth callback that bounced back with ?error=...
  const [callbackError, setCallbackError] = useState(null);
  const [busy, setBusy] = useState(null); // provider key with an in-flight action

  const { data, isLoading, error } = useQuery({
    queryKey: ["ops-connections"],
    queryFn: connections.list,
    refetchInterval: 10000,
    retry: false,
  });

  // OAuth callback landing: ?connected={provider} → success toast,
  // ?error=... → red banner. Either way scrub the params from the URL so a
  // refresh doesn't replay the message.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(window.location.search);
    const connected = sp.get("connected");
    const err = sp.get("error");
    if (connected) toast.success(`${providerName(connected)} connected`);
    if (err) setCallbackError(err);
    if (connected || err) {
      sp.delete("connected");
      sp.delete("error");
      const qs = sp.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
  }, []);

  const onConnect = async (key) => {
    setBusy(key);
    try {
      const r = await connections.connect(key);
      if (r?.authorize_url) {
        oauthRedirect.go(r.authorize_url);
        return; // navigating away — keep the button disabled
      }
      toast.error("No authorize URL returned — check the provider app config.");
    } catch (err2) {
      toast.error(err2?.response?.data?.detail || `Could not start the ${providerName(key)} OAuth flow`);
    }
    setBusy(null);
  };

  const onDisconnect = async (key) => {
    if (
      typeof window !== "undefined" &&
      !window.confirm(`Disconnect ${providerName(key)}? The stored token is revoked and deleted.`)
    ) {
      return;
    }
    setBusy(key);
    try {
      await connections.disconnect(key);
      toast.success(`${providerName(key)} disconnected`);
      qc.invalidateQueries({ queryKey: ["ops-connections"] });
    } catch (err2) {
      toast.error(err2?.response?.data?.detail || `Could not disconnect ${providerName(key)}`);
    }
    setBusy(null);
  };

  // The API may return a bare array or { connections: [...] } — index by slug.
  const rows = Array.isArray(data) ? data : data?.connections || data?.providers || data?.items || [];
  const byProvider = Object.fromEntries(rows.map((r) => [r.provider || r.name, r]));

  return (
    <div data-testid="page-connections" className="p-6 md:p-10">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
        <div className="min-w-0">
          <div className="pj-label mb-2">· Owner Accounts · OAuth connections</div>
          <h1 className="font-display text-[32px] md:text-[40px] leading-[1.02]">Connections</h1>
        </div>
        <div className="shrink-0">
          <MockBadge />
        </div>
      </div>

      <p className="mono text-[12px] text-[rgb(var(--dim))] mb-6 max-w-3xl">
        Connect the owner&apos;s social and email accounts once — tokens are stored encrypted and
        auto-refreshed by a background worker, and the social/email adapters use them automatically
        (env-var tokens still work as a fallback).
      </p>

      {callbackError && (
        <div
          data-testid="connections-error-banner"
          className="pj-card p-4 mb-6 mono text-[12px] flex items-start gap-2"
          style={{ color: "rgb(196,52,44)", border: "1px solid rgba(196,52,44,0.30)" }}
        >
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <span className="break-words">Connection failed: {callbackError}</span>
          <button
            type="button"
            data-testid="btn-dismiss-error"
            onClick={() => setCallbackError(null)}
            className="ml-auto mono text-[10px] uppercase tracking-[0.12em] text-[rgb(var(--dim))] hover:text-[rgb(var(--fg))] shrink-0"
          >
            Dismiss
          </button>
        </div>
      )}

      {error && (
        <div className="pj-card p-6 mono text-[12px] text-[rgb(196,52,44)]" data-testid="connections-load-error">
          Could not load connections (admin / ops roles only).
        </div>
      )}
      {isLoading && !data && (
        <div className="pj-card p-6 mono text-[12px] text-[rgb(var(--dim))]">Loading connections…</div>
      )}

      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PROVIDERS.map((p) => {
            const row = byProvider[p.key];
            const status = deriveStatus(row);
            const hasToken = ["connected", "expiring", "error"].includes(status);
            const env = envNames(p.key, row);
            // The backend list rows call it last_error; keep error as a fallback.
            const errorDetail = row?.error || row?.last_error;
            return (
              <div
                key={p.key}
                className="pj-card p-4 pj-card-hover overflow-hidden flex flex-col"
                data-testid={`connection-card-${p.key}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    aria-hidden="true"
                    className="mono shrink-0 inline-flex items-center justify-center"
                    style={{
                      width: 24,
                      height: 24,
                      fontSize: 11,
                      fontWeight: 700,
                      color: "rgb(var(--gold))",
                      border: "1px solid rgba(190,176,152,0.6)",
                      background: "rgba(244,239,230,0.75)",
                    }}
                  >
                    {p.mark}
                  </span>
                  <span className="mono text-[13px] text-[rgb(var(--fg))] truncate" title={p.name}>
                    {p.name}
                  </span>
                </div>

                <div className="mt-3">
                  <StatusBadge status={status} row={row} providerKey={p.key} />
                </div>

                {status === "connected" && row?.supports_refresh && (
                  <div
                    className="mono text-[10px] text-[rgb(var(--dim))] mt-2 uppercase tracking-[0.14em]"
                    data-testid={`connection-expires-${p.key}`}
                  >
                    Auto-refreshing · stays connected
                  </div>
                )}
                {row?.expires_at && status !== "not_configured" && !(status === "connected" && row?.supports_refresh) && (
                  <div
                    className="mono text-[10px] text-[rgb(var(--dim))] mt-2 uppercase tracking-[0.14em]"
                    data-testid={`connection-expires-${p.key}`}
                  >
                    Expires {fmtTime(row.expires_at)}
                  </div>
                )}

                {status === "error" && errorDetail && (
                  <div className="mono text-[10px] mt-2 break-words line-clamp-3" style={{ color: "rgb(196,52,44)" }} title={errorDetail}>
                    {errorDetail}
                  </div>
                )}

                {status === "not_configured" && (
                  <div
                    className="mono text-[10px] text-[rgb(var(--muted-2))] mt-2 break-words"
                    data-testid={`connection-env-${p.key}`}
                  >
                    Set{" "}
                    {env.map((e, i) => (
                      <span key={e}>
                        {i > 0 && " + "}
                        <code>{e}</code>
                      </span>
                    ))}{" "}
                    in the backend env to enable this provider.
                  </div>
                )}

                <div className="mt-4 pt-3 flex items-center gap-2" style={{ borderTop: "1px solid rgb(var(--ln))" }}>
                  <button
                    type="button"
                    data-testid={`btn-connect-${p.key}`}
                    disabled={status === "not_configured" || busy === p.key}
                    onClick={() => onConnect(p.key)}
                    className="mono inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={BTN_PRIMARY_STYLE}
                  >
                    {hasToken ? <Link2 size={11} /> : <Plug size={11} />}
                    {hasToken ? "Reconnect" : "Connect"}
                  </button>
                  {hasToken && (
                    <button
                      type="button"
                      data-testid={`btn-disconnect-${p.key}`}
                      disabled={busy === p.key}
                      onClick={() => onDisconnect(p.key)}
                      className="mono inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] hover:opacity-90 disabled:opacity-50"
                      style={BTN_SECONDARY_STYLE}
                    >
                      <Unplug size={11} /> Disconnect
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
